import { AnimatedButton } from '@dtx-company/shared-components/src/components/atoms/AnimatedButton/index'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { ButtonStyleProps } from '@dtx-company/shared-components/src'
import { EmailEntityType } from './types'
import { FC } from 'react'
import { FormFields, StyledFieldSet, Wrapper } from './styles'
import { Formik } from 'formik'
import { HeapContainers } from '../../../../constants/heap'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image/index'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { LayoutProps, SpaceProps } from 'styled-system'
import { MAX_EMAIL_LIMIT } from '../../../../constants/modals'
import { Section } from '@dtx-company/shared-components/src/components/atoms/Section/index'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { emailBatch } from '../EmailCode.utils'
import { getGqlError } from '../../../../utils/errors'
import { sendSuccessNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import events from '@dtx-company/inter-app/src/event-tracking/events/postDownload'
import isEmail from 'validator/lib/isEmail'

interface Props {
  batchId: string | undefined
  disabled?: boolean
  onClose?: () => void
  verbose?: boolean
  buttonProps?: ButtonStyleProps
  id?: string
  isCardVariant?: boolean
  entityType?: EmailEntityType
  submitFunction?: ((toEmails: string) => Promise<void>) | null
  flowcodeImageUrl?: string
}

export const EmailCodeForm: FC<Props & LayoutProps & SpaceProps> = ({
  disabled = false,
  batchId,
  onClose,
  verbose = true,
  buttonProps,
  id,
  isCardVariant = false,
  entityType = EmailEntityType.FLOWCODE,
  submitFunction = null,
  flowcodeImageUrl,
  ...rest
}: Props & LayoutProps & SpaceProps): JSX.Element => {
  const { jwt } = useAuthState()

  const entityTypeDisplay =
    entityType !== EmailEntityType.FLOWCODE ? entityType.toLowerCase() : entityType

  return (
    <Formik
      initialValues={{ email: jwt?.email ?? '' }}
      validate={values => {
        const errors: { email?: string } = {}
        if (!values.email) {
          errors.email = 'Required'
        } else if (values.email.split(',').some(address => !isEmail(address.trim()))) {
          errors.email = 'One or more invalid email address'
        }
        return errors
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const toEmails = values.email
            .split(',')
            .slice(0, MAX_EMAIL_LIMIT)
            .map(address => address.trim())
            .join(',')
          setSubmitting(true)
          const input = {
            batchId,
            toEmail: toEmails
          }
          submitFunction ? await submitFunction(toEmails) : await emailBatch(input)
          onClose && onClose()
          sendSuccessNotification('Email sent successfully!')
        } catch (e) {
          getGqlError(e)
        } finally {
          setSubmitting(false)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid
      }) => (
        <form onSubmit={handleSubmit}>
          <Section maxWidth="100%">
            <Wrapper
              maxWidth="450px"
              margin="0 auto"
              padding={['0 30px', '0 30px', '0']}
              display="block"
              {...rest}
            >
              <StyledFieldSet disabled={disabled}>
                {verbose && (
                  <>
                    <Text
                      variant={'body/large'}
                      textAlign="center"
                      marginBottom="9px"
                      marginTop="12px"
                      lineHeight="43px"
                    >
                      Email My {entityType}
                    </Text>
                    <Text
                      variant="body/small"
                      textAlign="center"
                      width={['92%', '92%', '410px']}
                      marginLeft={['4%', '4%', '17px']}
                    >
                      Email your {entityTypeDisplay} to yourself for easy mobile access, or share it
                      with a friend. Enter up to {MAX_EMAIL_LIMIT} comma separated emails.
                    </Text>
                    <Spacer mt="12px" />
                    <Text variant="general/captionAndLabel" fontWeight="bold" textAlign="center">
                      {`(note: if you have unsubscribed from emails, you will not receive the
                      ${entityTypeDisplay})`}
                    </Text>
                    <Spacer mt="32px" />
                    {flowcodeImageUrl && (
                      <Box justifyContent="center">
                        <Image src={flowcodeImageUrl} height="150px" width="150px" />
                      </Box>
                    )}
                  </>
                )}
                <FormFields>
                  <Input
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    label="Email Address"
                    width="100%"
                    maxWidth="initial"
                    placeholder="example@example.com"
                    error={!!errors.email && touched.email}
                    helperText={errors.email && touched.email ? errors.email : ''}
                    inputProps={{
                      name: 'email',
                      style: { textTransform: 'lowercase' }
                    }}
                    data-testid="email-input"
                  />
                </FormFields>
                <AnimatedButton
                  label={`Email My ${entityType}`}
                  textVariant="body/small"
                  fontWeight={600}
                  width="100%"
                  mt="24px"
                  disabled={!values.email || isSubmitting || !isValid}
                  loading={isSubmitting}
                  loadingProps={{ label: 'Emailing', width: 22, height: 22, marginTop: 11 }}
                  type="submit"
                  data-testid="submit-btn"
                  colorVariant="primaryBlack"
                  sizeVariant="mobileFullWidth"
                  marginBottom={isCardVariant ? 'unset' : '84px'}
                  maxWidth="100%"
                  onClick={events.userClickedEmailCodeButton}
                  id={id || `hp-${HeapContainers.EMAIL_FLOWCODE_MODAL}-email_code-btn`}
                  {...buttonProps}
                />
              </StyledFieldSet>
            </Wrapper>
          </Section>
        </form>
      )}
    </Formik>
  )
}
